import React from 'react';
import styled from 'styled-components';

import { fetchOrganization, fetchTeamPlayers, fetchInvitations } from './api';
import { useAsyncResource, getSuspension, unwrapResponse as unwrap } from './hooks';
import { isClubKind, useOrgKind, vocabularyForKind } from './vocabulary';
import SuspendedNotice from './SuspendedNotice';

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch (e) {
    return iso;
  }
};

const TeamAdminDashboard = () => {
  const storedKind = useOrgKind();
  const org = useAsyncResource(fetchOrganization, []);
  const players = useAsyncResource(fetchTeamPlayers, []);
  const invitations = useAsyncResource(fetchInvitations, []);

  const orgData = unwrap(org.data);
  const kind = orgData?.kind || storedKind;
  const isClub = isClubKind(kind);
  const v = vocabularyForKind(kind, orgData?.name);

  const rows = unwrap(players.data) || [];
  const invitationsList = unwrap(invitations.data) || [];

  // Roster rows are member-driven (Phase A): a row may have no mapping yet.
  const activeMembers = rows.filter((r) => r.member_active !== false).length;
  const activeMappings = rows.filter((r) => r.mapping_id && r.is_active).length;
  const inactiveMappings = rows.filter((r) => r.mapping_id && !r.is_active).length;
  const waitingToPick = rows.filter((r) => r.needs_player_pick).length;

  const unlimited = orgData ? (orgData.seats_unlimited || orgData.max_player_seats == null) : false;
  const maxSeats = orgData?.max_player_seats ?? 0;
  const seatsUsed = activeMappings;
  const seatsAvailable = Math.max(0, maxSeats - seatsUsed);
  const seatPct = maxSeats > 0 ? Math.min(100, Math.round((seatsUsed / maxSeats) * 100)) : 0;

  const suspension = getSuspension(org.error) || getSuspension(players.error) || getSuspension(invitations.error);

  const accessLabel = (() => {
    if (!orgData) return '';
    if (orgData.suspended_at) return v.cards.suspended;
    if (orgData.access_ends_at) return v.cards.until(formatDate(orgData.access_ends_at));
    return v.cards.openEnded;
  })();

  return (
    <Wrap>
      <Header>
        <Title>{v.roleTitle}</Title>
        {(orgData || suspension) && <Sub>{orgData?.name || suspension?.organization?.name}</Sub>}
      </Header>

      {suspension && <SuspendedNotice data={suspension} vocabulary={v} />}

      {!suspension && org.loading && <Muted>Loading {isClub ? 'club' : 'organization'}…</Muted>}
      {!suspension && org.error && <ErrorBox>Failed to load {isClub ? 'club' : 'organization'} details.</ErrorBox>}

      {!suspension && orgData && isClub && (
        <Cards>
          <Card>
            <CardTitle>{v.cards.access}</CardTitle>
            <CardMetric>{activeMembers}</CardMetric>
            <CardSub>No player limit</CardSub>
          </Card>
          <Card>
            <CardTitle>{v.cards.waiting}</CardTitle>
            <CardMetric>{waitingToPick}</CardMetric>
            <CardSub>{v.cards.waitingHint}</CardSub>
          </Card>
          <Card>
            <CardTitle>{v.cards.pending}</CardTitle>
            <CardMetric>{invitationsList.length}</CardMetric>
            <CardSub>{v.pendingHint}</CardSub>
          </Card>
          <Card>
            <CardTitle>{v.cards.period}</CardTitle>
            <CardMetric $small>{accessLabel}</CardMetric>
            <CardSub>{orgData.is_operational ? 'Active' : 'Switched off'}</CardSub>
          </Card>
        </Cards>
      )}

      {!suspension && orgData && !isClub && (
        <Cards>
          <Card>
            <CardTitle>Seat usage</CardTitle>
            <CardMetric>{seatsUsed} / {unlimited ? '∞' : (maxSeats || '—')}</CardMetric>
            <CardSub>{unlimited ? 'No seat limit' : `${seatsAvailable} available`}</CardSub>
            {!unlimited && maxSeats > 0 && (
              <SeatBar>
                <SeatBarFill style={{ width: `${seatPct}%` }} />
              </SeatBar>
            )}
          </Card>
          <Card>
            <CardTitle>{v.cards.access}</CardTitle>
            <CardMetric>{activeMappings}</CardMetric>
            <CardSub>{inactiveMappings} deactivated</CardSub>
          </Card>
          <Card>
            <CardTitle>{v.cards.pending}</CardTitle>
            <CardMetric>{invitationsList.length}</CardMetric>
            <CardSub>{v.pendingHint}</CardSub>
          </Card>
          <Card>
            <CardTitle>{v.cards.period}</CardTitle>
            <CardMetric $capitalize>
              {orgData.subscription_tier || 'basic'}
            </CardMetric>
            <CardSub>{orgData.is_active ? 'Active' : 'Inactive'}</CardSub>
          </Card>
        </Cards>
      )}
    </Wrap>
  );
};

export default TeamAdminDashboard;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Title = styled.h1`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 24px;
  margin: 0;
  color: ${(props) => props.theme.white};
`;

const Sub = styled.div`
  color: ${(props) => props.theme.blueish2};
  font-size: 14px;
`;

const Muted = styled.div`
  color: ${(props) => props.theme.blueish2};
  font-size: 14px;
  padding: 8px 0;
`;

const ErrorBox = styled.div`
  color: ${(props) => props.theme.white};
  background: #3a1414;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
`;

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
`;

const Card = styled.div`
  background: ${(props) => props.theme.row};
  border: 1px solid ${(props) => props.theme.lines};
  border-radius: 12px;
  padding: 18px;
`;

const CardTitle = styled.div`
  color: ${(props) => props.theme.blueish2};
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-family: 'SatoshiMedium', sans-serif;
`;

const CardMetric = styled.div`
  margin: 8px 0 4px;
  font-size: ${(props) => (props.$small ? '20px' : '28px')};
  font-family: 'SatoshiBold', sans-serif;
  color: ${(props) => props.theme.green};
  ${(props) => props.$capitalize && 'text-transform: capitalize;'}
`;

const CardSub = styled.div`
  color: ${(props) => props.theme.blueish2};
  font-size: 13px;
`;

const SeatBar = styled.div`
  margin-top: 12px;
  height: 6px;
  border-radius: 999px;
  background: ${(props) => props.theme.lines};
  overflow: hidden;
`;

const SeatBarFill = styled.div`
  height: 100%;
  background: ${(props) => props.theme.green};
`;
